import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {share} from 'rxjs/operators';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {Currency, SiteConnector} from '@spartacus/core';
import {LeadFormContractDataModel} from '../lead-form.model';
import {FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {UserType} from '@app/product/models/dcp-user-type.model';
import {MomentService, ValidationService} from '@dcp/configurator';
import {LeadFormContractDetailsValidationService} from './services/contract-details-validation.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'dcp-lead-form-contract-details',
  templateUrl: './lead-form-contract-details.component.html',
  styleUrls: ['./lead-form-contract-details.component.scss'],
})
export class LeadFormContractDetailsComponent implements OnInit, OnDestroy {

  public currencies$: Observable<Currency[]>;
  private totalValueSubscription: Subscription = Subscription.EMPTY;
  private expectedStartDateSubscription: Subscription = Subscription.EMPTY;
  private expectedEndDateSubscription: Subscription = Subscription.EMPTY;
  @Input() leadFormContractDataModel: LeadFormContractDataModel;
  @Input() userType: UserType;
  @Input() sapEmployeeSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** tracks if the onSubmit function has been called */
  @Input() submitted = false;

  public contractDetailsForm: FormGroup;

  constructor(
    private siteConnector: SiteConnector,
    public validationService: ValidationService,
    public momentService: MomentService,
    private fb: FormBuilder,
    private readonly leadFormContractDetailsValidationService: LeadFormContractDetailsValidationService,
  ) {
  }

  /**
   * Validation pattern to check whether the date is of format DD-MMM-yyyy.
   */
  private static getDatesValidationPattern() {
    return /^(([1-9])|(0[1-9])|([1-2]\d)|(3[0-1]))-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/;
  }

  ngOnInit() {
    this.contractDetailsForm = this.fb.group({
      rangeStart: [null, [Validators.required, this.createDateFormatValidatorFn()]],
      rangeEnd: [null, [Validators.required, this.createDateFormatValidatorFn()]],
      expectedTotalValue: [null, [Validators.required, this.leadFormContractDetailsValidationService.createNumber2DecimalsValidator()]],
      currency: [null, [Validators.required]],
    }, {validator: this.leadFormContractDetailsValidationService.createDatesRangeValidatorFn()});
    this.setupPricingSubscriptions();
    this.setupDatesSubscriptions();
  }

  ngOnDestroy() {
    this.totalValueSubscription.unsubscribe();
    this.expectedStartDateSubscription.unsubscribe();
    this.expectedEndDateSubscription.unsubscribe();
  }

  setupPricingSubscriptions() {
    this.currencies$ = this.siteConnector.getCurrencies().pipe(share());
    this.totalValueSubscription = this.contractDetailsForm.get('expectedTotalValue').valueChanges.pipe(untilDestroyed(this)).subscribe(totalValue => {
      this.leadFormContractDataModel.totalRevenue = totalValue;
    });
  }

  setupDatesSubscriptions() {
    this.expectedStartDateSubscription = this.contractDetailsForm.get('rangeStart').valueChanges.pipe(untilDestroyed(this)).subscribe(startDate => {
      if (startDate) {
        this.leadFormContractDataModel.contractStartDate = this.momentService.ngbDateToMoment(startDate).format('YYYY-MM-DD');
      }
    });
    this.expectedEndDateSubscription = this.contractDetailsForm.get('rangeEnd').valueChanges.pipe(untilDestroyed(this)).subscribe(endDate => {
      if (endDate) {
        this.leadFormContractDataModel.contractEndDate = this.momentService.ngbDateToMoment(endDate).format('YYYY-MM-DD');
      }
    });
  }

  /**
   * Checks if a validation error exists for the validator name.
   *
   * @param formControl InputComponent to check for errors.
   * @param validatorName error to look after.
   * @return true if showError is true and the errors array has a value that equals the validatorName.
   */
  hasValidationFailed(formControl: { errors: object }, validatorName: string): boolean {
    return this.validationService.hasValidationFailed(formControl, validatorName, this.submitted);
  }

  /**
   * Country change handler
   */
  onCurrencyChange(chosenCurrencyArray: string[]): void {
    this.leadFormContractDataModel.currency = chosenCurrencyArray[0];
  }

  /**
   * Getting a boundary date for the error message when date range is violated.
   *
   * @param rangeSpecifier range of dates including start date and end date.
   */
  getErrorDate(rangeSpecifier: string) {
    const date = this.contractDetailsForm.get(rangeSpecifier);
    if (date && date.value) {
      const errorDate: string = this.momentService.ngbDateToMoment(date.value).format('DD-MMM-YYYY');
      return errorDate === 'Invalid date' ? rangeSpecifier === 'rangeStart' ? 'Expected End Date' : 'Expected Start Date' : errorDate;
    }
    return null;
  }

  /**
   * Validate the date from the date picker inside the form group.
   * If the date is not of the format dd-MMM-yyyy, then the validation is not passed.
   */
  private createDateFormatValidatorFn(): ValidationErrors {
    return (ctrl: FormControl) => {
      if (ctrl.value) {
        const date = ctrl.value;
        return (!date || !LeadFormContractDetailsComponent.getDatesValidationPattern().test(this.momentService.ngbDateToMoment(date).format('DD-MMM-yyyy'))) ?
          {format: true} :
          null;
      }
      return null;
    };
  }
}
